import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DoneIcon from "@mui/icons-material/Done";
import GradingIcon from "@mui/icons-material/Grading";
import GroupIcon from "@mui/icons-material/Group";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import InfoIcon from "@mui/icons-material/Info";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import MessageIcon from "@mui/icons-material/Message";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import PageviewIcon from "@mui/icons-material/Pageview";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import RemoveDoneIcon from "@mui/icons-material/RemoveDone";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ThumbsUpDownIcon from "@mui/icons-material/ThumbsUpDown";
import WorkIcon from "@mui/icons-material/Work";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { styled } from "@mui/material/styles";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import relativeTimeLabel from "_shared/dates/relative-time-label";
import * as Constants from "config";
import {
  useMarkAllNotificationsAsSeenMutation,
  useMarkNotificationAsSeenMutation,
  useMarkNotificationAsUnseenMutation,
} from "notifications/_api/notifications-api";
import NotificationQuery, {
  NotificationFilterParameters,
  NotificationOrderField,
  NotificationOrderParameters,
} from "notifications/_api/queries/notification-query";
import {
  isNotificationNavigable,
  NotificationLink,
  NotificationNavigationState,
  NotificationType,
} from "notifications/notification-type";
import {
  addNotification,
  getNotificationsByQuery,
  isNotificationLoadIssue,
  markAllNotificationsSeenForUser,
  markNotificationSeenForUser,
  markNotificationUnseenForUser,
  NotificationLoadIssue,
  populateNotificationsByQuery,
} from "notifications/_stores/notifications-redux-slice";
import NotificationContent from "notifications/_views/notification-content";
import { Action } from "notifications/notification-action";
import { BoxType } from "notifications/notification-box-type";
import TopicCategory from "notifications/topics/topic-category";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import type Notification from "notifications/notification";
import { handleNotificationMutationFailure } from "notifications/notification-error";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { useSelector } from "react-redux";

/**
 * Icon for a proposal notification's `actionRequired`.
 *
 * Every branch returns an icon, including the default one. Enumerating the known actions and leaving
 * the rest blank would mean that any action this bundle does not have a case for — one the API added
 * since the bundle was built, for example — renders an empty icon slot next to a perfectly readable
 * notification.
 */
function proposalActionIcon(actionRequired: string | undefined) {
  switch (actionRequired) {
    case Action.AcceptOrReject:
      return <ThumbsUpDownIcon />;
    case Action.Review:
    case Action.DraftProposalReview:
    case Action.ProposalReview:
    case Action.ClientTeamLeaderReview:
    case Action.ProposalReReviewRequested:
    case Action.ReviewOrReject:
      return <PageviewIcon />;
    case Action.DraftProposalReviewApproved:
    case Action.ProposalReviewApproved:
      return <GradingIcon />;
    case Action.ProposalRevisionReview:
    case Action.Hired:
      return <AssignmentIcon />;
    case Action.HireOrCancel:
      return <WorkIcon />;
    case Action.ProposalReviewerAccessChanged:
    case Action.DraftProposalReviewerAccessChanged:
      return <ManageAccountsIcon />;
    case Action.ProposalReviewerRemoved:
      return <PersonRemoveIcon />;
    default:
      return <InfoIcon />;
  }
}

/** The icon standing for a notification's topic, in whichever slot the row's layout gives it. */
function categoryIcon(notification: NotificationType) {
  switch (notification.topic?.category) {
    case TopicCategory.Messaging.Forum:
      return <MessageIcon />;
    case TopicCategory.Users.NetworkInvitation:
      return <PersonAddIcon />;
    case TopicCategory.Users.NetworkConnection:
      return <AccountTreeIcon />;
    case TopicCategory.Work.Proposal:
      return proposalActionIcon(notification.actionRequired);
    case TopicCategory.Work.Agreement:
      return <WorkIcon />;
    case TopicCategory.Marketplace.Team:
      return <GroupIcon />;
    case TopicCategory.LegalEntities.EntityMember:
      return <PersonIcon />;
    case TopicCategory.Marketplace.TeamInvitation:
      return <GroupAddIcon />;
    case TopicCategory.LegalEntities.LegalEntity:
      return <SupervisorAccountIcon />;
    default:
      return null;
  }
}

const BellIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  margin: theme.spacing(0.5),
}));
const NotificationMenu = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    backdropFilter: "blur(10px) brightness(105%)",
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
  zIndex: theme.zIndex.modal + 1,
}));
const MarkAllReadButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0.4),
  marginLeft: theme.spacing(1.6),
}));
const ViewAllButton = styled(Button)(() => ({
  borderRadius: 0,
  width: "100%",
}));
// The Popover's own Paper is capped at `calc(100% - 32px)`, so a flat `theme.spacing(44)` here is
// wider than the Paper containing it on any viewport under 384px and the list overflows its own
// menu. The clamp asks for the same 352px wherever there is room for it and gives way to the
// Paper's cap on a phone, where a fixed width has nowhere to go.
const NotificationList = styled(List)(({ theme }) => ({
  maxHeight: theme.spacing(40),
  overflow: "auto",
  padding: 0,
  width: `min(${theme.spacing(44)}, calc(100vw - ${theme.spacing(4)}))`,
}));
/**
 * Unread is nearly opaque white; read stays the half-transparent grey.
 *
 * Unread was `rgba(255,255,255,.5)`, which is not white on this menu — the Paper is
 * backdrop-blurred and half transparent, so the page behind grey-washes every row and the two
 * states arrived at nearly the same colour. At .92 an unread row reads as white while still
 * belonging to the glass: measured against this menu's stack it drifts about 8 of 255 between
 * sitting over a pale part of the page and over a dark one, where opaque drifts none and .75
 * drifts 27 — enough to start passing for a read row.
 *
 * Do not mistake this for the read-state cue. Over a *pale* page the two backgrounds differ by
 * about 6 of 255 whatever alpha is chosen, opaque included, because both end up near white; the
 * background only separates them over something dark. Weight is what actually carries read state —
 * see `NotificationTitle`.
 */
function notificationItemBackground(seen: boolean | undefined) {
  return seen ? 'rgba(243, 243, 243, 0.5)' : 'rgba(255, 255, 255, 0.92)';
}
/** Hover keeps the read ordering: each state darkens from its own base rather than onto one colour. */
function notificationItemHoverBackground(seen: boolean | undefined) {
  return seen ? '#e8e8e8' : '#f5f5f5';
}
/**
 * Class the read-state control carries so a row can reveal it on hover.
 *
 * A plain class rather than an emotion component selector, which needs the babel plugin this build
 * does not run, and rather than a `seen`-style prop, which would make the row re-render on hover.
 */
const rowActionClass = "AhNotificationRowAction";
/**
 * Reveals the row's read-state control on a pointer device, where the row under the cursor is the
 * one the control belongs to.
 *
 * `opacity` rather than `display` or `visibility`, for two reasons. The 28px stays reserved, so the
 * title beside it cannot reflow — and possibly wrap — as the pointer arrives. And an element hidden
 * by opacity is still in the accessibility tree and still focusable, so a screen reader and the tab
 * key both keep a control that `visibility: hidden` would have taken away from them entirely.
 */
const rowCategoryIconClass = "AhNotificationRowCategoryIcon";
/**
 * On a pointer device the read-state control takes the category icon's place while the row is
 * hovered, so it costs the row no width of its own at all.
 *
 * `pointer-events: none` while it is faded out matters: the two are stacked in one grid cell, so
 * without it the invisible button would sit over the icon and swallow a click meant for the row.
 *
 * `opacity` rather than `display`, so the button stays focusable and in the accessibility tree —
 * a keyboard user tabs to it and `:focus-within` brings it into view.
 */
const revealRowActionOnHover = {
  [`& .${rowActionClass}`]: {
    "@media (hover: hover)": {
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 120ms ease-in-out",
    },
  },
  [`& .${rowCategoryIconClass}`]: {
    "@media (hover: hover)": {
      transition: "opacity 120ms ease-in-out",
    },
  },
  [`&:hover .${rowActionClass}, &:focus-within .${rowActionClass}`]: {
    opacity: 1,
    pointerEvents: "auto",
  },
  [`&:hover .${rowCategoryIconClass}, &:focus-within .${rowCategoryIconClass}`]: {
    "@media (hover: hover)": {
      opacity: 0,
    },
  },
};
/** Stacks the category icon and the read-state control in one cell so they can trade places. */
const LeadingSlot = styled("div")(() => ({
  alignItems: "center",
  display: "inline-grid",
  justifyItems: "center",
  "& > *": {
    gridArea: "1 / 1",
  },
}));
const CategoryIcon = styled("span")(() => ({
  alignItems: "center",
  display: "inline-flex",
}));
/**
 * Paint the row's own background under its divider.
 *
 * MUI's `divider` ships `background-clip: padding-box`, so the 1px border is NOT covered by the
 * row's background and shows whatever is behind the row instead. That is invisible on an opaque
 * surface and very visible on this one: the Paper is half transparent and backdrop-blurred, so
 * wherever the menu crosses something dark on the page — the dashboard's navy marketplace banner —
 * that 1px strip went almost black while the dividers above and below it stayed grey.
 *
 * It only became visible when unread rows turned opaque. While they were `rgba(255,255,255,.5)` the
 * row and its divider were washed by the same background and the line blended in.
 */
const dividerOverOwnBackground = {
  backgroundClip: "border-box",
} as const;
const NotificationItem = styled(ListItemButton, { shouldForwardProp: (prop) => prop !== "seen" })<{
  seen?: boolean;
}>(({ seen }) => ({
  backgroundColor: notificationItemBackground(seen),
  ...dividerOverOwnBackground,
  "&:first-of-type": {
    borderTop: "none",
  },
  "&:last-child": {
    borderBottom: "none",
  },
  "&:hover": {
    backgroundColor: notificationItemHoverBackground(seen),
  },
  ...revealRowActionOnHover,
}));
/**
 * A notification with nowhere to go when it is clicked (#4849). It is a plain list item: no button
 * role, no tab stop, no pointer cursor, and no hover highlight promising something to open.
 */
const ReadOnlyNotificationItem = styled(ListItem, { shouldForwardProp: (prop) => prop !== "seen" })<{
  seen?: boolean;
}>(({ seen }) => ({
  backgroundColor: notificationItemBackground(seen),
  ...dividerOverOwnBackground,
  cursor: "default",
  "&:first-of-type": {
    borderTop: "none",
  },
  "&:last-child": {
    borderBottom: "none",
  },
  ...revealRowActionOnHover,
}));
const TitleContainer = styled("div")(() => ({
  alignItems: "baseline",
  display: "flex",
  justifyContent: "space-between",
}));
// A row's title has about 240px to share with this label, and "Sep 10 10:10pm" wanted 89px of it —
// enough that "Network Connection" and the message titles wrapped to two lines. A relative label
// costs a third of that and is what the reader wants from a notification anyway; the exact
// timestamp is a hover away, and still spelled out in full on the communications page.
const SentDate = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  flexShrink: 0,
  marginLeft: theme.spacing(1),
  textAlign: "right",
  whiteSpace: "nowrap",
}));
// `margin-left: auto` pushes this to the row's trailing edge regardless of how wide the icon and
// text ahead of it end up being, rather than relying on ListItemText's own flex sizing.
const ReadStateButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== "trailing" })<{
  trailing?: boolean;
}>(({ trailing, theme }) => ({
  alignSelf: "center",
  flexShrink: 0,
  // Only the trailing placement needs pushing to the row's edge; in the leading slot it is a grid
  // child sharing one cell with the category icon.
  marginLeft: trailing ? "auto" : undefined,
  padding: theme.spacing(0.5),
}));
/**
 * Says the row goes somewhere, which is the one thing its read state cannot say.
 *
 * Read and unread are told apart by weight, and those two states are independent of whether a row
 * opens anything — a notification can be any of the four combinations. This is the second axis, and
 * it is the same arrow the dashboard's recent-activity card puts on exactly the rows that carry a
 * link, on a card listing these same notifications.
 *
 * The slot is reserved on every row, not only on the ones that fill it. Dropping it from the rest
 * would leave their text column wider, and the sent dates down the list would then right-align to
 * two different edges.
 *
 * `aria-hidden`, because a navigable row is already a `role="button"` and a read-only one is a
 * plain list item (#4849). Assistive technology is told by the markup; the arrow is for the eye.
 */
const NavigationAffordance = styled("span")(({ theme }) => ({
  alignItems: "center",
  alignSelf: "center",
  display: "inline-flex",
  flexShrink: 0,
  justifyContent: "center",
  width: theme.spacing(3),
}));
/**
 * An unread notification is set in medium, a read one in the body's ordinary weight.
 *
 * The two states were previously told apart only by a background of `rgba(243,243,243,.5)` against
 * `rgba(255,255,255,.5)` — twelve points of grey at half opacity, over a backdrop-blurred menu that
 * is showing the page through it. That difference is close to invisible, and the read state is the
 * one thing a notification list has to make obvious.
 */
const NotificationTitle = styled("span", { shouldForwardProp: (prop) => prop !== "seen" })<{
  seen?: boolean;
}>(({ seen, theme }) => ({
  fontWeight: seen ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium,
}));

function getNotificationTitle(notification: NotificationType, now: Date, seen: boolean) {
  let title: string;
  const sentDate = notification.createdDate;
  if (notification.topic?.category === TopicCategory.Messaging.Forum) {
    // The sender is the title. "Message from " cost 101px of the 181px a title has once the
    // timestamp is reserved — more than half the row spent restating what the message icon beside it
    // already says — and it pushed every ordinary name onto a second line.
    //
    // The count only appears above one. A run of messages from one sender in one forum is folded
    // into the row already sitting unread (#830), so "(3)" is a real count of what this row stands
    // for. "(1)" would not be: a count of one means the row has not absorbed another, which is also
    // what a reader sees when folding was refused — the recipients disagree about having read it, or
    // the forum's membership moved between two messages — and those refusals produce a *second row*
    // rather than a count of two. Two rows each claiming "(1)" would assert something untrue about a
    // pair of messages. The communications page suppresses the count at one for the same reason.
    const senderName = notification.messageInfo?.senderName?.value ?? "Unknown Sender";
    const messageCount = notification.occurrenceCount;
    title = messageCount > 1 ? `${senderName} (${messageCount})` : senderName;
  } else {
    title =
      Constants.notificationTypes[(notification.topic?.category ?? "") as keyof typeof Constants.notificationTypes] ??
      "Notification";
  }
  return (
    <TitleContainer>
      <NotificationTitle seen={seen}>{title}</NotificationTitle>
      {sentDate && (
        <Tooltip title={sentDate.format("MMM D, YYYY h:mma")}>
          <SentDate variant="caption">{relativeTimeLabel(sentDate, now)}</SentDate>
        </Tooltip>
      )}
    </TitleContainer>
  );
}

/**
 * One row of the bell's dropdown. Rows whose notification has somewhere to go are buttons; the rest
 * — network connection announcements today (#4849) — are plain list items. Either kind gets its own
 * "Mark as read" control while it is unread, so clearing one notification never requires "Mark All
 * As Read" (#4950).
 */
function NotificationListItem({
  notification,
  seen,
  now,
  onSelect,
  onMarkAsRead,
  onMarkAsUnread,
  revealsOnHover,
}: Readonly<{
  notification: NotificationType;
  seen: boolean;
  now: Date;
  revealsOnHover: boolean;
  onSelect: (event: React.MouseEvent<HTMLElement>, notification: NotificationType) => void;
  onMarkAsRead: (event: React.MouseEvent<HTMLElement>, notification: NotificationType) => void;
  onMarkAsUnread: (event: React.MouseEvent<HTMLElement>, notification: NotificationType) => void;
}>) {
  /*
    One control, placed by whichever layout is in force — never two in the tree at once, so there is
    only ever one "Mark as read" for a keyboard or a screen reader to find. It is present in both
    read states and names the act it performs, so a row marked read by mistake can be put back
    without leaving the menu. The tooltip is applied to the button itself rather than to a wrapper,
    so the button stays the grid or flex child that its own layout positions.
  */
  const readStateControl = (
    <Tooltip title={seen ? "Mark as unread" : "Mark as read"}>
      <ReadStateButton
        className={rowActionClass}
        trailing={!revealsOnHover}
        aria-label={seen ? "Mark as unread" : "Mark as read"}
        size="small"
        onClick={(event) => {
          // The row itself may also have an onClick that navigates; this control's job is to change
          // the notification's read state in place, not to follow it anywhere.
          event.stopPropagation();
          if (seen) onMarkAsUnread(event, notification);
          else onMarkAsRead(event, notification);
        }}
      >
        {seen ? <RemoveDoneIcon fontSize="small" /> : <DoneIcon fontSize="small" />}
      </ReadStateButton>
    </Tooltip>
  );

  const content = (
    <React.Fragment>
      <ListItemIcon>
        {revealsOnHover ? (
          <LeadingSlot>
            <CategoryIcon className={rowCategoryIconClass}>{categoryIcon(notification)}</CategoryIcon>
            {readStateControl}
          </LeadingSlot>
        ) : (
          categoryIcon(notification)
        )}
      </ListItemIcon>
      <ListItemText<"span", "div">
        primary={getNotificationTitle(notification, now, seen)}
        secondary={<NotificationContent notification={notification} />}
        slotProps={{ secondary: { component: "div" } }}
      />
      {/* On touch it keeps its own place at the trailing edge, beside the arrow. */}
      {!revealsOnHover && readStateControl}
      <NavigationAffordance aria-hidden>
        {isNotificationNavigable(notification) && <ArrowForwardRoundedIcon color="action" fontSize="small" />}
      </NavigationAffordance>
    </React.Fragment>
  );

  if (!isNotificationNavigable(notification)) {
    return (
      <ReadOnlyNotificationItem seen={seen} divider>
        {content}
      </ReadOnlyNotificationItem>
    );
  }

  return (
    <NotificationItem seen={seen} divider onClick={(event) => onSelect(event, notification)}>
      {content}
    </NotificationItem>
  );
}

export default function NotificationBell() {
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  // Ids the per-row "Mark as read" control has a request out for, so a second click on the same row
  // before the first settles does not send the mutation twice (#4950). This is a ref, not state, on
  // purpose: it must never take part in deciding whether a row reads as read. That answer comes from
  // the store alone, which `markNotificationSeenForUser` updates synchronously on click, so a
  // notification marked unread again elsewhere resurfaces here instead of being masked by a local
  // overlay the bell would only drop on failure or remount.
  const markSeenRequestsInFlight = React.useRef<Set<string>>(new Set());
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  const interfaceName = "notificationBell";
  const query = useMemo(() => {
    return new NotificationQuery({
      filterParameters: new NotificationFilterParameters({
        boxType: BoxType.Inbox,
        isUnread: true,
      }),
      orderParameters: new NotificationOrderParameters({
        orderBy: NotificationOrderField.CreatedDate,
        orderDir: OrderDirection.Descending,
      }),
      paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 25 }),
    });
  }, []);

  const notifications = useSelector(getNotificationsByQuery(query));

  // Read once per render and handed to every row, so two rows sent in the same minute can never
  // disagree about how long ago that was.
  const now = new Date();

  // Whether the read-state control is revealed by hovering the row, in the category icon's place,
  // or simply sits at the trailing edge beside the arrow. Asked once for the whole menu rather than
  // once per row, and asked of the pointer rather than of a width: a narrow desktop window still
  // has a cursor to hover with, and a large tablet still has none.
  const revealsOnHover = useMediaQuery("(hover: hover)");

  const dispatch = useAttorneyHubDispatch();
  const [markNotificationAsSeen] = useMarkNotificationAsSeenMutation();
  const [markAllNotificationsAsSeen] = useMarkAllNotificationsAsSeenMutation();
  const [markNotificationAsUnseen] = useMarkNotificationAsUnseenMutation();

  // A notification the bundle could not deserialize renders as an error row with no read state and no
  // way to be marked read, so counting it as unread would strand the badge at a number the user
  // cannot clear and leave "Mark All As Read" enabled with nothing to do.
  const numUnread =
    notifications?.data.filter(
      (notification: Notification | NotificationLoadIssue) =>
        !isNotificationLoadIssue(notification) && !notification.isSeenByUser(session.user?.id)
    ).length ?? 0;

  useEffect(() => {
    dispatch(populateNotificationsByQuery({ session, query, interfaceName }));
  }, [dispatch, query, session, session.user?.id?.value]);

  useEffect(() => {
    if (notifications === undefined) {
      dispatch(populateNotificationsByQuery({ session, query, interfaceName }));
    }
  }, [dispatch, notifications, query, session]);

  function handleBellClick(event: React.MouseEvent<HTMLButtonElement>) {
    setMenuAnchor(menuAnchor ? null : event.currentTarget);
  }

  async function handleNotificationClick(_event: React.MouseEvent<HTMLElement>, notification: NotificationType) {
    try {
      const seenNotification = await markNotificationAsSeen({ notificationId: notification.id.value }).unwrap();
      if (!seenNotification) throw new Error("Notification not returned from API");
      dispatch(addNotification(seenNotification));

      // A new-message notification carries the forum as its topic (#3144), so `NotificationLink`
      // resolves the forum route from the topic id alone — there is no longer a messaging special
      // case here that had to dig the forum out of `messageInfo` and could only apologize with a
      // snackbar when those details were missing.
      const redirectUrl = NotificationLink(notification);
      if (!redirectUrl || redirectUrl === globalThis.location.pathname) return;
      const navigationState = NotificationNavigationState(notification, `${location.pathname}${location.search}`);
      if (navigationState) {
        navigate(redirectUrl, { state: navigationState });
      } else {
        navigate(redirectUrl);
      }
    } catch (error) {
      await handleNotificationMutationFailure({
        error,
        operation: "mark-seen",
        refresh: async () => {
          await dispatch(populateNotificationsByQuery({ session, query, interfaceName }));
        },
        notify: (message) => enqueueSnackbar(message, { variant: "error" }),
      });
    } finally {
      setMenuAnchor(null);
    }
  }

  async function handleMarkAsReadClick(_event: React.MouseEvent<HTMLElement>, notification: NotificationType) {
    const notificationId = notification.id;
    if (markSeenRequestsInFlight.current.has(notificationId.value)) return;
    markSeenRequestsInFlight.current.add(notificationId.value);

    // Optimistic: the non-invalidating reducer flips the row and the badge before the request
    // resolves, without clearing everything else the bell has cached (#4950).
    dispatch(markNotificationSeenForUser({ notificationId, userId: session.user?.id }));

    // A `throw` inside a `try` bails the whole component out of the React Compiler, so the
    // request result is carried out on a flag and the rollback runs after the `try` instead.
    let markedSeen = false;
    try {
      const seenNotification = await markNotificationAsSeen({ notificationId: notificationId.value }).unwrap();
      markedSeen = Boolean(seenNotification);
      if (!markedSeen) console.error("Notification not returned from API");
    } catch (error) {
      console.error(error);
    }

    markSeenRequestsInFlight.current.delete(notificationId.value);
    if (markedSeen) return;

    dispatch(markNotificationUnseenForUser({ notificationId, userId: session.user?.id }));
    enqueueSnackbar("Couldn't mark that notification as read. Please try again.", { variant: "error" });
  }

  /**
   * The mirror of `handleMarkAsReadClick`, for a row the reader has just marked read and wants back.
   *
   * It shares `markSeenRequestsInFlight`, deliberately: the two directions act on the same row and
   * the same field, so a second click while either is still out would race the first regardless of
   * which way each was going.
   */
  async function handleMarkAsUnreadClick(_event: React.MouseEvent<HTMLElement>, notification: NotificationType) {
    const notificationId = notification.id;
    if (markSeenRequestsInFlight.current.has(notificationId.value)) return;
    markSeenRequestsInFlight.current.add(notificationId.value);

    dispatch(markNotificationUnseenForUser({ notificationId, userId: session.user?.id }));

    // As above: the result is carried out on a flag so no `throw` sits inside the `try`.
    let markedUnseen = false;
    try {
      const unseenNotification = await markNotificationAsUnseen({ notificationId: notificationId.value }).unwrap();
      markedUnseen = Boolean(unseenNotification);
      if (!markedUnseen) console.error("Notification not returned from API");
    } catch (error) {
      console.error(error);
    }

    markSeenRequestsInFlight.current.delete(notificationId.value);
    if (markedUnseen) return;

    dispatch(markNotificationSeenForUser({ notificationId, userId: session.user?.id }));
    enqueueSnackbar("Couldn't mark that notification as unread. Please try again.", { variant: "error" });
  }

  async function handleMarkAllClick() {
    try {
      if (numUnread < 1) return;
      await markAllNotificationsAsSeen().unwrap();
      dispatch(markAllNotificationsSeenForUser(session.user?.id));
    } catch (error) {
      await handleNotificationMutationFailure({
        error,
        operation: "mark-all-seen",
        refresh: async () => {
          await dispatch(populateNotificationsByQuery({ session, query, interfaceName }));
        },
        notify: (message) => enqueueSnackbar(message, { variant: "error" }),
      });
    }
  }

  function handleViewAllClick() {
    setMenuAnchor(null);
    if (location.pathname.startsWith("/communications")) return;
    navigate("/communications");
  }

  return (
    <React.Fragment>
      <BellIconButton aria-label="open notifications menu" onClick={handleBellClick} size="large">
        <Badge overlap="rectangular" badgeContent={numUnread > 0 ? numUnread : null} color="error">
          <NotificationsIcon />
        </Badge>
      </BellIconButton>
      <NotificationMenu
        anchorEl={menuAnchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {/* The span is what a disabled button needs to still report hover to the tooltip, so the
            "nothing to mark" wording is readable exactly when the button cannot be pressed. */}
        <Tooltip
          title={
            numUnread < 1 ? "No unread notifications to mark as read" : "Mark every unread notification as read"
          }
        >
          <span>
            <MarkAllReadButton
              color="primary"
              startIcon={<PlaylistAddCheckIcon />}
              disabled={numUnread < 1}
              onClick={handleMarkAllClick}
            >
              Mark All As Read
            </MarkAllReadButton>
          </span>
        </Tooltip>
        <Divider />
        <NotificationList>
          {(!notifications || notifications.data.length < 1) && (
            <NotificationItem seen={true} disabled>
              <ListItemIcon>
                <NotificationsOffOutlinedIcon />
              </ListItemIcon>
              <ListItemText>No Unread Notifications</ListItemText>
            </NotificationItem>
          )}
          {notifications?.data.map((notification: NotificationType | NotificationLoadIssue) =>
            isNotificationLoadIssue(notification) ? (
              <Typography key={notification.id.value} color="error" sx={{ padding: 2 }}>
                Issue loading this notification.
              </Typography>
            ) : (
              <NotificationListItem
                key={notification.id.value}
                notification={notification}
                seen={notification.isSeenByUser(session.user?.id)}
                now={now}
                onSelect={(event, selected) => {
                  void handleNotificationClick(event, selected);
                }}
                onMarkAsRead={(event, selected) => {
                  void handleMarkAsReadClick(event, selected);
                }}
                onMarkAsUnread={(event, selected) => {
                  void handleMarkAsUnreadClick(event, selected);
                }}
                revealsOnHover={revealsOnHover}
              />
            )
          )}
        </NotificationList>
        <Divider />
        <ViewAllButton color="primary" onClick={handleViewAllClick}>
          View All
        </ViewAllButton>
      </NotificationMenu>
    </React.Fragment>
  );
}
